import { hexToPixel } from '../../game/world-map/terrial-engine/HexUtils'
import { HEX_COL_SPACING } from '../../game/world-map/terrial-engine/types'
import type { CastleData } from '../../game/world-map/terrial-engine/types'
import { getFlagColor } from '../../game/world-map/terrial-engine/constants'

/**
 * Manual density knob for the espionage world map at camera.zoom === 1.
 * Raise for more space between castles; lower for a denser map.
 */
export const ESPIONAGE_V2_MAP_SCALE_TUNING = 0.16

export interface EspionageV2MapViewport {
  width: number
  height: number
}

export interface EspionageV2MapTransform {
  minX: number
  minY: number
  scale: number
  offsetX: number
  offsetY: number
  contentW: number
  contentH: number
}

export interface EspionageV2MapCamera {
  panX: number
  panY: number
  zoom: number
}

export const ESPIONAGE_V2_MAP_ZOOM_MIN = 1
export const ESPIONAGE_V2_MAP_ZOOM_MAX = 14

/**
 * World pixels → espionage map pixels at camera.zoom === 1.
 * Derived from label width vs adjacent-hex spacing; scaled by {@link ESPIONAGE_V2_MAP_SCALE_TUNING}.
 */
export function getEspionageV2MapPixelsPerWorldUnit(): number {
  const labels = getEspionageMapLabelMetrics(ESPIONAGE_V2_MAP_ZOOM_MIN)
  const desiredGapPx = labels.maxTextWidth * 0.95
  return (desiredGapPx / HEX_COL_SPACING) * ESPIONAGE_V2_MAP_SCALE_TUNING
}

/**
 * Visual scale of the canvas on screen (0–1 when the espionage page is scale-to-fit).
 * On some mobile browsers `getBoundingClientRect` ignores ancestor CSS transforms;
 * fall back to {@link pageScale} from the page shell when measured scale looks too large.
 */
export function getEspionageMapCanvasVisualScale(
  canvas: HTMLCanvasElement,
  viewport: EspionageV2MapViewport,
  pageScale: number,
): number {
  const rect = canvas.getBoundingClientRect()
  if (rect.width <= 0 || viewport.width <= 0) {
    return pageScale > 0 ? pageScale : 1
  }
  const measured = rect.width / viewport.width
  if (pageScale > 0 && measured > pageScale * 1.12) {
    return pageScale
  }
  return measured > 0 ? measured : pageScale > 0 ? pageScale : 1
}

/** Screen/client coords → canvas internal pixels (accounts for page scale transform). */
export function espionageClientToCanvasPoint(
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number,
  visualScale: number,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect()
  const scale = visualScale > 0 ? visualScale : 1
  return {
    x: (clientX - rect.left) / scale,
    y: (clientY - rect.top) / scale,
  }
}

/** Pin + alliance flag sizing — scales linearly with map zoom (matches map geometry). */
export function getEspionageMapMarkerMetrics(zoom: number): {
  scale: number
  pinRadius: number
  ownPinRadius: number
  pinShadowOffset: number
  pinAnchorLift: number
  flagWidth: number
  flagGap: number
  selectedRingExtra: number
  ownRingExtra: number
  strokeWidth: number
  commanderStarOuter: number
  commanderStarInner: number
  commanderStarOffsetY: number
  hitRadiusPx: number
} {
  const scale = Math.max(ESPIONAGE_V2_MAP_ZOOM_MIN, Math.min(ESPIONAGE_V2_MAP_ZOOM_MAX, zoom))
  return {
    scale,
    pinRadius: Math.max(3, 5.5 * scale),
    ownPinRadius: Math.max(4, 7 * scale),
    pinShadowOffset: 0.8 * scale,
    pinAnchorLift: 4 * scale,
    flagWidth: Math.max(12, 26 * scale),
    flagGap: 2 * scale,
    selectedRingExtra: 4 * scale,
    ownRingExtra: 2.5 * scale,
    strokeWidth: Math.max(1, 1.25 * scale),
    commanderStarOuter: Math.max(4, 6 * scale),
    commanderStarInner: Math.max(2, 2.8 * scale),
    commanderStarOffsetY: Math.max(18, 30 * scale),
    hitRadiusPx: Math.max(12, 18 * scale),
  }
}

/** Screen-space label sizing for castle owner / alliance names (scales with zoom). */
export function getEspionageMapLabelMetrics(zoom: number): {
  lordFontSize: number
  legionFontSize: number
  maxTextWidth: number
  labelTopOffset: number
  legionLineGap: number
  shadowOffset: number
} {
  const scale = Math.max(ESPIONAGE_V2_MAP_ZOOM_MIN, Math.min(ESPIONAGE_V2_MAP_ZOOM_MAX, zoom))
  return {
    lordFontSize: Math.max(6, Math.min(20, Math.round(9 * scale))),
    legionFontSize: Math.max(5, Math.min(18, Math.round(8 * scale))),
    maxTextWidth: Math.max(56, Math.min(220, Math.round(88 * scale))),
    labelTopOffset: Math.max(5, Math.min(28, Math.round(7 * scale))),
    legionLineGap: Math.max(8, Math.min(36, Math.round(11 * scale))),
    shadowOffset: Math.max(0.5, Math.min(2, 0.8 * scale)),
  }
}

export function createEspionageV2MapCamera(): EspionageV2MapCamera {
  return { panX: 0, panY: 0, zoom: 1 }
}

export function applyEspionageV2MapCamera(
  ctx: CanvasRenderingContext2D,
  viewport: EspionageV2MapViewport,
  camera: EspionageV2MapCamera,
): void {
  const cx = viewport.width / 2
  const cy = viewport.height / 2
  ctx.translate(cx + camera.panX, cy + camera.panY)
  ctx.scale(camera.zoom, camera.zoom)
  ctx.translate(-cx, -cy)
}

/** Base map coords → screen coords (canvas px). */
export function espionageMapToScreenPoint(
  mapX: number,
  mapY: number,
  viewport: EspionageV2MapViewport,
  camera: EspionageV2MapCamera,
): { x: number; y: number } {
  const cx = viewport.width / 2
  const cy = viewport.height / 2
  return {
    x: (mapX - cx) * camera.zoom + cx + camera.panX,
    y: (mapY - cy) * camera.zoom + cy + camera.panY,
  }
}

/** Screen coords (canvas px) → base map coords (before camera). */
export function espionageScreenToMapPoint(
  screenX: number,
  screenY: number,
  viewport: EspionageV2MapViewport,
  camera: EspionageV2MapCamera,
): { x: number; y: number } {
  const cx = viewport.width / 2
  const cy = viewport.height / 2
  return {
    x: (screenX - cx - camera.panX) / camera.zoom + cx,
    y: (screenY - cy - camera.panY) / camera.zoom + cy,
  }
}

export function zoomEspionageMapAtScreen(
  camera: EspionageV2MapCamera,
  viewport: EspionageV2MapViewport,
  screenX: number,
  screenY: number,
  factor: number,
): void {
  const cx = viewport.width / 2
  const cy = viewport.height / 2
  const mapX = (screenX - cx - camera.panX) / camera.zoom + cx
  const mapY = (screenY - cy - camera.panY) / camera.zoom + cy
  const nextZoom = Math.max(
    ESPIONAGE_V2_MAP_ZOOM_MIN,
    Math.min(ESPIONAGE_V2_MAP_ZOOM_MAX, camera.zoom * factor),
  )
  camera.zoom = nextZoom
  camera.panX = screenX - cx - (mapX - cx) * nextZoom
  camera.panY = screenY - cy - (mapY - cy) * nextZoom
}

export function getEspionageVisibleMapBounds(
  viewport: EspionageV2MapViewport,
  camera: EspionageV2MapCamera,
  margin = 48,
): { minX: number; minY: number; maxX: number; maxY: number } {
  const tl = espionageScreenToMapPoint(0, 0, viewport, camera)
  const tr = espionageScreenToMapPoint(viewport.width, 0, viewport, camera)
  const bl = espionageScreenToMapPoint(0, viewport.height, viewport, camera)
  const br = espionageScreenToMapPoint(viewport.width, viewport.height, viewport, camera)
  return {
    minX: Math.min(tl.x, tr.x, bl.x, br.x) - margin,
    minY: Math.min(tl.y, tr.y, bl.y, br.y) - margin,
    maxX: Math.max(tl.x, tr.x, bl.x, br.x) + margin,
    maxY: Math.max(tl.y, tr.y, bl.y, br.y) + margin,
  }
}

export function flagColorNumberToCss(color: number): string {
  const n = color >>> 0
  const r = (n >> 16) & 0xff
  const g = (n >> 8) & 0xff
  const b = n & 0xff
  return `rgb(${r}, ${g}, ${b})`
}

/** Distinct marker colors — same alliance id always maps to the same entry. */
const ALLIANCE_MARKER_PALETTE: readonly number[] = [
  0xb83232, 0x2a6fdb, 0x2d8a4e, 0xc47f17, 0x7b3fa0, 0x1a8a8a, 0xc45c8a, 0x5c6bc0,
  0x8d6e3a, 0x00695c, 0xd84315, 0x455a64, 0x6a1b9a, 0x1565c0, 0x558b2f, 0xad1457,
]

export function hashAllianceId(allianceId: string): number {
  let hash = 5381
  for (let i = 0; i < allianceId.length; i++) {
    hash = (hash * 33) ^ allianceId.charCodeAt(i)
  }
  return hash >>> 0
}

export function getAllianceMarkerColor(allianceId: string): number {
  const id = allianceId.trim()
  if (!id) return 0x888888
  return ALLIANCE_MARKER_PALETTE[hashAllianceId(id) % ALLIANCE_MARKER_PALETTE.length]
}

export function getMarkerColorForCastle(
  castle: CastleData,
  allianceColors: Map<string, number>,
): number {
  const allianceId = castle.allianceId ? String(castle.allianceId).trim() : ''
  if (allianceId && allianceColors.has(allianceId)) {
    return allianceColors.get(allianceId)!
  }
  if (allianceId) {
    const color = getAllianceMarkerColor(allianceId)
    allianceColors.set(allianceId, color)
    return color
  }
  return getFlagColor(castle)
}

export function buildAllianceMarkerColorMap(castles: CastleData[]): Map<string, number> {
  const map = new Map<string, number>()
  for (const castle of castles) {
    const allianceId = castle.allianceId ? String(castle.allianceId).trim() : ''
    if (allianceId && !map.has(allianceId)) {
      map.set(allianceId, getAllianceMarkerColor(allianceId))
    }
  }
  return map
}

function markerColorLuminance(color: number): number {
  const r = ((color >>> 0) >> 16) & 0xff
  const g = ((color >>> 0) >> 8) & 0xff
  const b = (color >>> 0) & 0xff
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255
}

/** Alliance label fill + shadow for readable contrast on the map. */
export function getAllianceLabelColors(markerColor: number): { fill: string; shadow: string } {
  const fill = flagColorNumberToCss(markerColor)
  const shadow =
    markerColorLuminance(markerColor) > 0.52
      ? 'rgba(18, 10, 6, 0.88)'
      : 'rgba(248, 242, 228, 0.92)'
  return { fill, shadow }
}

export function getCastleWorldPixel(castle: CastleData): { x: number; y: number } {
  return hexToPixel(castle.q, castle.r)
}

/** Bounding box in world pixels that contains every castle, with padding. */
export function computeCastleWorldBounds(
  castles: CastleData[],
  padding = 120,
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (castles.length === 0) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const castle of castles) {
    if (castle.q == null || castle.r == null) continue
    const { x, y } = getCastleWorldPixel(castle)
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x)
    maxY = Math.max(maxY, y)
  }

  if (!Number.isFinite(minX)) return null

  return {
    minX: minX - padding,
    minY: minY - padding,
    maxX: maxX + padding,
    maxY: maxY + padding,
  }
}

/**
 * Place all castles on a large map at a fixed world scale (geographic density).
 * The panel viewport pans/zooms over this content; it no longer fits every castle on screen at zoom 1.
 */
export function computeEspionageV2MapTransform(
  castles: CastleData[],
  _viewport?: EspionageV2MapViewport,
  padding = 16,
): EspionageV2MapTransform | null {
  const bounds = computeCastleWorldBounds(castles)
  if (!bounds) return null

  const worldW = bounds.maxX - bounds.minX
  const worldH = bounds.maxY - bounds.minY
  if (worldW <= 0 || worldH <= 0) return null

  const scale = getEspionageV2MapPixelsPerWorldUnit()
  const contentW = worldW * scale
  const contentH = worldH * scale

  return {
    minX: bounds.minX,
    minY: bounds.minY,
    scale,
    offsetX: padding,
    offsetY: padding,
    contentW,
    contentH,
  }
}

export function worldToEspionageMapPoint(
  worldX: number,
  worldY: number,
  transform: EspionageV2MapTransform,
): { x: number; y: number } {
  return {
    x: (worldX - transform.minX) * transform.scale + transform.offsetX,
    y: (worldY - transform.minY) * transform.scale + transform.offsetY,
  }
}

export function castleToEspionageMapPoint(
  castle: CastleData,
  transform: EspionageV2MapTransform,
): { x: number; y: number } {
  const { x, y } = getCastleWorldPixel(castle)
  return worldToEspionageMapPoint(x, y, transform)
}

export interface EspionageV2MapContentBounds {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

/** Logical content rect in map pixels (castle cluster span at the fixed world scale). */
export function getTransformContentBounds(
  transform: EspionageV2MapTransform,
  padding = 24,
): EspionageV2MapContentBounds {
  return {
    minX: transform.offsetX - padding,
    minY: transform.offsetY - padding,
    maxX: transform.offsetX + transform.contentW + padding,
    maxY: transform.offsetY + transform.contentH + padding,
  }
}

/** Map-pixel bounds covering all castles (where targets can exist). */
export function computeCastleClusterMapBounds(
  castles: CastleData[],
  transform: EspionageV2MapTransform,
  padding = 32,
): EspionageV2MapContentBounds | null {
  if (castles.length === 0) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const castle of castles) {
    if (!hasCastleHex(castle)) continue
    const { x, y } = castleToEspionageMapPoint(castle, transform)
    minX = Math.min(minX, x)
    minY = Math.min(minY, y)
    maxX = Math.max(maxX, x)
    maxY = Math.max(maxY, y)
  }

  if (!Number.isFinite(minX)) return getTransformContentBounds(transform, padding)

  return {
    minX: minX - padding,
    minY: minY - padding,
    maxX: maxX + padding,
    maxY: maxY + padding,
  }
}

export function hasCastleHex(castle: CastleData): boolean {
  const q = Number(castle.q)
  const r = Number(castle.r)
  return Number.isFinite(q) && Number.isFinite(r)
}

export function normalizeCastleHex(castle: CastleData): CastleData {
  return {
    ...castle,
    q: Number(castle.q),
    r: Number(castle.r),
  }
}

/**
 * Keep pan/zoom inside the castle cluster — no scrolling through empty world.
 */
export function clampEspionageMapCameraToCluster(
  camera: EspionageV2MapCamera,
  viewport: EspionageV2MapViewport,
  content: EspionageV2MapContentBounds,
  slack = 24,
): void {
  const z = camera.zoom
  const cx = viewport.width / 2
  const cy = viewport.height / 2
  const padMinX = content.minX - slack
  const padMaxX = content.maxX + slack
  const padMinY = content.minY - slack
  const padMaxY = content.maxY + slack
  const contentW = padMaxX - padMinX
  const contentH = padMaxY - padMinY
  const visW = viewport.width / z
  const visH = viewport.height / z

  const visMinX = cx - (cx + camera.panX) / z
  const visMaxX = visMinX + visW
  if (visW >= contentW) {
    if (visMinX > padMinX) camera.panX += z * (padMinX - visMinX)
    if (visMaxX < padMaxX) camera.panX += z * (padMaxX - visMaxX)
  } else {
    const clampedVisMinX = Math.max(padMinX, Math.min(padMaxX - visW, visMinX))
    camera.panX = z * (cx - clampedVisMinX) - cx
  }

  const visMinY = cy - (cy + camera.panY) / z
  const visMaxY = visMinY + visH
  if (visH >= contentH) {
    if (visMinY > padMinY) camera.panY += z * (padMinY - visMinY)
    if (visMaxY < padMaxY) camera.panY += z * (padMaxY - visMaxY)
  } else {
    const clampedVisMinY = Math.max(padMinY, Math.min(padMaxY - visH, visMinY))
    camera.panY = z * (cy - clampedVisMinY) - cy
  }
}

/** Pan so a map point appears at the viewport center (e.g. player's castle). */
export function centerEspionageMapOnPoint(
  mapX: number,
  mapY: number,
  viewport: EspionageV2MapViewport,
  camera: EspionageV2MapCamera,
): void {
  const cx = viewport.width / 2
  const cy = viewport.height / 2
  camera.panX = (cx - mapX) * camera.zoom
  camera.panY = (cy - mapY) * camera.zoom
}

export function getCastleFlagCssColor(castle: CastleData): string {
  return flagColorNumberToCss(getFlagColor(castle))
}

export function buildAllianceLeaderWalletSet(
  alliances: Array<{ _id: string; leaderWalletAddress?: string }>,
): Map<string, string> {
  const map = new Map<string, string>()
  for (const a of alliances) {
    const leader = String(a.leaderWalletAddress || '').toLowerCase().trim()
    if (leader) map.set(String(a._id), leader)
  }
  return map
}

export function isLegionCommanderCastle(
  castle: CastleData,
  leaderByAllianceId: Map<string, string>,
): boolean {
  const allianceId = castle.allianceId ? String(castle.allianceId) : ''
  if (!allianceId) return false
  const leader = leaderByAllianceId.get(allianceId)
  if (!leader) return false
  return String(castle.ownerId || '').toLowerCase().trim() === leader
}
